import type { Messaging } from 'firebase/messaging'
import { parseUserAgent } from '@st/utils'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, onMessage } from 'firebase/messaging'

const FIREBASE_SW_PATH = '/firebase-messaging-sw.js'

export const useWebPushStore = defineStore('webPush', () => {
  const { open } = useToast()
  const { t } = useNuxtApp().$i18n
  const { vapidKey, ...firebaseConfig } = useRuntimeConfig().public.firebase
  const router = useRouter()
  const route = useRoute()
  const { os, browser } = parseUserAgent()

  const permission = ref<NotificationPermission>(
    !!globalThis.Notification && Notification.permission,
  )
  const isEnableNotifications = computed(() => permission.value === 'granted')
  const isDisabledNotifications = computed(() => permission.value === 'denied')

  function showTurnOnWebPushToast() {
    open({
      label: t('profile.notifications.enableWebPush'),
      type: 'info',
    })
  }

  async function requestNotificationPermission() {
    permission.value = await Notification.requestPermission()
    if (isDisabledNotifications.value) {
      showTurnOnWebPushToast()
    }
  }

  const swRegistration = ref<ServiceWorkerRegistration>()
  async function getServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      console.error('navigator does not support serviceWorker')
      return
    }
    swRegistration.value = await navigator.serviceWorker.getRegistration()
    swRegistration.value ??= await navigator.serviceWorker.register(
      FIREBASE_SW_PATH,
      {
        type: 'module',
      },
    )
  }

  const messaging = ref<Messaging | null>(null)
  function registerFirebaseApp() {
    const app = initializeApp(firebaseConfig)
    messaging.value = getMessaging(app)
  }

  const hasFirebaseInstance = computed(() => !!messaging.value)

  async function registerConnection() {
    await getServiceWorker()
    registerFirebaseApp()
  }

  const subscribeToken = useLocalStorage<string>('subscribeToken', '')
  async function subscribe() {
    if (!hasFirebaseInstance.value) {
      await registerConnection()
    }
    const token = await getToken(messaging.value!, {
      serviceWorkerRegistration: swRegistration.value,
      vapidKey,
    })

    const { error } = useStFetch('/web-push/subscribe', {
      method: 'post',
      body: {
        token,
      },
    })
    if (!error.value) {
      subscribeToken.value = token
    }
  }

  async function unsubscribe() {
    if (!hasFirebaseInstance.value) {
      await registerConnection()
    }
    const { error } = useStFetch('/web-push/unsubscribe', {
      method: 'post',
      body: {
        token: subscribeToken.value,
      },
    })
    if (!error.value) {
      subscribeToken.value = ''
    }
  }

  const hasSubscription = computed({
    get: () => !!subscribeToken.value,
    set: (value: boolean) => {
      if (value) {
        subscribe()
      } else {
        unsubscribe()
      }
    },
  })

  async function init() {
    await requestNotificationPermission()
    if (!isEnableNotifications.value) return
    await registerConnection()
    await subscribe()
  }

  function webPushTurnOn() {
    if (isDisabledNotifications.value) {
      router.replace({
        query: { ...route.query, modal: 'webPushInstruction', os, browser },
      })
    } else {
      init()
    }
  }

  watch(
    () => messaging.value,
    (value) => {
      if (!value) {
        return
      }
      onMessage(value, (payload) => {
        if (!swRegistration.value || !subscribeToken.value) {
          return
        }
        const notificationTitle = payload.notification?.title
        const notificationOptions: NotificationOptions = {
          body: payload.notification?.body,
          data: {
            link: payload.fcmOptions?.link,
          },
        }
        swRegistration.value.showNotification(
          notificationTitle || '',
          notificationOptions,
        )
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data.action === 'redirectFromWebPush' && event.data.url) {
            router.push(event.data.url)
          }
        })
      })
    },
  )

  return {
    requestNotificationPermission,
    hasSubscription,
    isEnableNotifications,
    isDisabledNotifications,
    messaging,
    init,
    unsubscribe,
    swRegistration,
    registerConnection,
    webPushTurnOn,
    showTurnOnWebPushToast,
  }
})
